import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Icon,
  Input,
  InputNumber,
  Radio,
  Select,
  Switch,
  Table,
  Tabs,
  Tooltip,
  message,
} from 'antd';
import {
  ChartDataTab,
  ChartFontsizeSettings,
  ChartGeneralSettings,
  ChartStylesSettings,
  ChartTypeContainer,
  ChartTypeSettings,
  CollapseContainer,
  EditableTable,
} from '../styles';
import {
  ChartTypesIcon,
  FontSettingsIcon,
  GeneralSettingsIcon,
  ArrowDown,
  chartStylesIcon,
} from '../chartIcons';
import { FontstyleCombobox } from './fontStyleComboBox';
import { fetchChart, getChartPayload, getUniqueRgb } from '../chartHelper';
import { EditableCell } from './editableCell';
import { CHART_TRANSLATION } from '../translationValues';
import { LABEL_TYPES, getBarChartStyles, getBarChartTypesList } from '../chartConstants';
import regex from '../../../../../utils/regex';
import { debounce } from 'underscore';
import { deepCopy } from '../../../../../utils/javascript';
import { ChartColorCell } from './chartColorCell';
import {
  ChartCollapse,
  ChartSelect,
  CollapsePanel,
  OverlayHeader,
  panelStyle,
  useCheckTableInFocus,
} from './chartSidebarHelper';

const { TabPane } = Tabs;
const { Option } = Select;

export const BarChartSettings = ({ element, updateComplexElementDetails, t, globalVariables, isInteractiveDesign }) => {
  const { contents, uuid, style } = element;
  const {
    type,
    labels,
    seriesList,
    showLegend,
    showLabels,
    showGrid,
    legendPosition,
    prefixValue,
    suffixValue,
    valueInterval,
    labelFontSize,
    valueFontSize,
    legendFontSize,
    labelColor,
    valueColor,
    legendColor,
    chartStyle,
    baseVariable,
    labelVariable,
    seriesNameVariable,
    seriesValueVariable,
    orientation,
  } = contents;

  const { isVariable } = style;
  const [chartName, setChartName] = useState(baseVariable);
  const [prefixStateValue, setPrefixStateValue] = useState(prefixValue);
  const [suffixStateValue, setSuffixStateValue] = useState(suffixValue);
  const [rangeIntervalValue, setRangeIntervalValue] = useState(valueInterval);
  const chartTypesList = useMemo(() => getBarChartTypesList(t), []);
  const chartStyles = useMemo(() => getBarChartStyles(type, t), [type]);
  const allowedVariables = useMemo(() => {
    const filterVariableList = [labelVariable, seriesNameVariable, seriesValueVariable];
    return globalVariables.filter(({ type, placeHolderText }) => {
      if (type === 'String' && !filterVariableList.includes(placeHolderText)) {
        return true;
      }
      return false;
    });
  }, [labelVariable, seriesNameVariable, seriesValueVariable, globalVariables]);
  const [tableRef, tableKey] = useCheckTableInFocus();

  useEffect(() => {
    setChartName(baseVariable);
  }, [baseVariable]);

  const getColumns = () => {
    const columnList = [
      {
        title: '',
        dataIndex: 'more',
        key: 'more',
        width: 50,
        render: (text, record, index) => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <ChartColorCell index={index} deleteRow={deleteRow} addRow={addRow} t={t} />
          </div>
        ),
      },
      {
        title: (
          <div style={{ paddingLeft: '8px' }}>
            {t(CHART_TRANSLATION.LABEL.key, CHART_TRANSLATION.LABEL.value)}
          </div>
        ),
        dataIndex: 'label',
        key: 'label',
        width: 100,
        render: (text, record, index) => (
          <EditableCell
            record={record}
            text={text}
            id="label"
            updateSource={(record, id, content) => updateSource(record, id, content, index)}
          />
        ),
      },
    ];
    seriesList.forEach(({ name }, index) => {
      columnList.push({
        title: () => (
          <OverlayHeader
            text={name}
            id={name}
            updateSource={(record, key, changedValue) => updateSeriesName(changedValue, index)}
            deleteColumn={() => deleteColumn(index)}
          />
        ),
        dataIndex: name,
        key: name,
        width: 100,
        render: (text, record, index) => (
          <EditableCell
            record={record}
            text={text}
            id={name}
            updateSource={(record, id, content) => updateSource(record, id, content, index)}
          />
        ),
      });
    });

    return columnList;
  };

  const columns = useMemo(() => getColumns(), [seriesList]);

  const dataSource = useMemo(() => {
    return labels.map((label, index) => {
      const singleSource = {
        label: label,
        color: [0, 0, 0],
      };
      seriesList.forEach(({ name, series }) => {
        singleSource[name] = series[index] || '';
      });
      return singleSource;
    });
  }, [labels, seriesList]);

  const updateChartDetails = async (updatedValue, key, skipChartCreation = false) => {
    let copiedContent;
    if (key) {
      copiedContent = deepCopy(element).contents;
      copiedContent[key] = updatedValue;
    } else {
      copiedContent = updatedValue;
    }
    const payload = getChartPayload(copiedContent, isVariable);
    try {
      let data;
      if (!skipChartCreation) {
        data = await fetchChart(payload);
        copiedContent.dataUrl = `data:image/png;base64, ${data.base64ImageBytes}`;
      }
      copiedContent.dataRequest = payload;
      updateComplexElementDetails(uuid, copiedContent, 'contents');
    } catch (error) {
      message.error(
        t(CHART_TRANSLATION.CHART_UPDATE_ERROR.key, CHART_TRANSLATION.CHART_UPDATE_ERROR.value),
      );
    }
  };

  const updateChartWithStyle = async (copiedContent, copiedStyle) => {
    const payload = getChartPayload(copiedContent, copiedStyle.isVariable);
    try {
      copiedContent.dataRequest = payload;
      updateComplexElementDetails(
        uuid,
        { contents: copiedContent, style: copiedStyle },
        'contents-with-style',
      );
    } catch (error) {
      message.error(
        t(CHART_TRANSLATION.CHART_UPDATE_ERROR.key, CHART_TRANSLATION.CHART_UPDATE_ERROR.value),
      );
    }
  };

  const debouncedUpdateChartDetails = useCallback(debounce(updateChartDetails, 300), [
    seriesList,
    labels,
  ]);

  const updateSource = (record, key, changedValue, index) => {
    if (key === 'label') {
      const tempLabels = labels.map((label, labelIndex) => {
        if (labelIndex === index) {
          return changedValue;
        }
        return label;
      });
      updateChartDetails(tempLabels, 'labels');
    } else {
      const tempSeriesList = seriesList.map(seriesItem => {
        if (key === seriesItem.name) {
          seriesItem.series = seriesItem.series.map((seriesValue, seriesIndex) => {
            if (index === seriesIndex) {
              return changedValue;
            }
            return seriesValue;
          });
        }
        return seriesItem;
      });
      updateChartDetails(tempSeriesList, 'seriesList');
    }
  };

  const updateSeriesName = (changedValue, index) => {
    const tempSeriesList = deepCopy(seriesList).map((item, itemIndex) => {
      if (index === itemIndex) {
        item.name = changedValue;
      }
      return item;
    });
    updateChartDetails(tempSeriesList, 'seriesList');
  };

  const addColumn = () => {
    const seriesListLength = seriesList.length;
    const newSeries = {
      name: `Series ${seriesListLength + 1}`,
      series: [...seriesList[seriesListLength - 1].series],
      color: getUniqueRgb(seriesList.map(({ color }) => color)),
    };
    const newSeriesList = deepCopy([...seriesList, newSeries]);
    updateChartDetails(newSeriesList, 'seriesList');
    //scroll to newly added rule in the table.
    const timeout = setTimeout(() => {
      if (seriesList.length >= 2) {
        const ruleHeader = document.getElementById(`Series ${seriesListLength + 1}`);
        ruleHeader && ruleHeader.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      clearTimeout(timeout);
    }, 500);
  };

  const addRow = index => {
    const newLabels = [...labels.slice(0, index), '', ...labels.slice(index)];
    const newSeriesList = seriesList.map(seriesItem => {
      const { series } = seriesItem;
      seriesItem.series = [...series.slice(0, index), '', ...series.slice(index)];
      return seriesItem;
    });
    const copiedContent = deepCopy(contents);
    copiedContent.seriesList = newSeriesList;
    copiedContent.labels = newLabels;
    updateChartDetails(copiedContent);
  };

  const deleteRow = index => {
    const newLabels = labels.filter((l, labelIndex) => labelIndex !== index);
    const newSeriesList = seriesList.map(seriesItem => {
      const { series } = seriesItem;
      seriesItem.series = series.filter((l, seriesIndex) => seriesIndex !== index);
      return seriesItem;
    });
    const copiedContent = deepCopy(contents);
    copiedContent.seriesList = newSeriesList;
    copiedContent.labels = newLabels;
    updateChartDetails(copiedContent);
  };

  const deleteColumn = index => {
    const newSeriesList = seriesList.filter((item, sIndex) => sIndex !== index);
    const copiedContent = deepCopy(contents);
    copiedContent.seriesList = newSeriesList;
    updateChartDetails(copiedContent);
  };

  const toggleLegend = checked => {
    if (chartStyle === LABEL_TYPES.WITH_DESCRIPTION) return;
    updateChartDetails(checked, 'showLegend');
  };

  const toggleGrid = checked => {
    updateChartDetails(checked, 'showGrid');
  };

  const toggleLabels = checked => {
    updateChartDetails(checked, 'showLabels');
  };

  const setOrientation = value => {
    updateChartDetails(value, 'orientation');
  };

  const setLegendPosition = value => {
    updateChartDetails(value, 'legendPosition');
  };

  const setPrefixValue = e => {
    setPrefixStateValue(e.target.value);
    debouncedUpdateChartDetails(e.target.value, 'prefixValue');
  };

  const setSuffixValue = e => {
    setSuffixStateValue(e.target.value);
    debouncedUpdateChartDetails(e.target.value, 'suffixValue');
  };

  const setIntervalValue = value => {
    if (typeof value === 'number' && value !== valueInterval) {
      setRangeIntervalValue(value);
      debouncedUpdateChartDetails(value, 'valueInterval');
    }
  };

  const setLabelFontsize = value => {
    updateChartDetails(value, 'labelFontSize');
  };

  const setValueFontsize = value => {
    updateChartDetails(value, 'valueFontSize');
  };

  const setLegendFontsize = value => {
    updateChartDetails(value, 'legendFontSize');
  };

  const setChartStyle = value => {
    const { contents: copiedContent } = deepCopy(element);
    copiedContent.chartStyle = value;
    updateChartDetails(copiedContent);
  };

  const setLabelFontColor = ({ rgb }) => {
    updateChartDetails([rgb.r, rgb.g, rgb.b], 'labelColor');
  };

  const setValueFontColor = ({ rgb }) => {
    updateChartDetails([rgb.r, rgb.g, rgb.b], 'valueColor');
  };

  const setLegendFontColor = ({ rgb }) => {
    updateChartDetails([rgb.r, rgb.g, rgb.b], 'legendColor');
  };

  const setChartType = ({ type }) => {
    const { contents: copiedContent } = deepCopy(element);
    copiedContent.type = type;
    copiedContent.chartStyle = 'normal';
    updateChartDetails(copiedContent);
  };

  const setDynamicChart = value => {
    const { style: copiedStyle, contents: copiedContent } = deepCopy(element);
    copiedStyle.isVariable = value;
    updateChartWithStyle(copiedContent, copiedStyle);
  };

  const setBaseVariable = e => {
    if (regex.NO_SPLCHAR_EXCEPT_HYPHEN.test(e.target.value)) {
      message.error(t(CHART_TRANSLATION.SPECIAL_CHAR_ERROR.key, CHART_TRANSLATION.SPECIAL_CHAR_ERROR.value));
      return;
    }
    setChartName(e.target.value);
    debouncedUpdateChartDetails(e.target.value, 'baseVariable', true);
  };

  const setLabelVariable = value => {
    updateChartDetails(value, 'labelVariable', true);
  };

  const setSeriesNameVariable = value => {
    updateChartDetails(value, 'seriesNameVariable', true);
  };

  const setSeriesValueVariable = value => {
    updateChartDetails(value, 'seriesValueVariable', true);
  };

  return (
    <Tabs defaultActiveKey="1" style={{ overflow: 'unset' }}>
      <TabPane
        tab={t(CHART_TRANSLATION.DATA_LABEL.key, CHART_TRANSLATION.DATA_LABEL.value)}
        key="1"
      >
        <ChartDataTab>
          <label id="dynamic-chart-switch">
            <span>
              {t(
                CHART_TRANSLATION.DYNAMIC_CHART_QUESTION.key,
                CHART_TRANSLATION.DYNAMIC_CHART_QUESTION.value,
              )}
            </span>
            <Switch checked={!!isVariable} onChange={checked => setDynamicChart(checked)} disabled={isInteractiveDesign}></Switch>
          </label>
          {!isVariable ? (
            <>
              <EditableTable ref={tableRef}>
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  pagination={false}
                  size="middle"
                  bordered
                  tableLayout="fixed"
                  key={tableKey}
                  scroll={{ x: true }}
                />
                <Tooltip
                  title={t(
                    CHART_TRANSLATION.ADD_COLUMN_LABEL.key,
                    CHART_TRANSLATION.ADD_COLUMN_LABEL.value,
                  )}
                >
                  <Button type="link" onClick={addColumn} style={{ padding: '0 5px' }}>
                    <Icon type="plus" />
                  </Button>
                </Tooltip>
              </EditableTable>
              <Button type="link" onClick={() => addRow(dataSource.length)}>
                <Icon type="plus" />{' '}
                {t(CHART_TRANSLATION.ADD_ROW_LABEL.key, CHART_TRANSLATION.ADD_ROW_LABEL.value)}
              </Button>
            </>
          ) : (
            <div>
              <label>
                <span>
                  {t(CHART_TRANSLATION.NAME_LABEL.key, CHART_TRANSLATION.NAME_LABEL.value)}
                </span>
                <Input value={chartName} onChange={setBaseVariable} />
              </label>
              <label>
                <span>{t(CHART_TRANSLATION.LABEL.key, CHART_TRANSLATION.LABEL.value)}</span>
                <ChartSelect t={t} value={labelVariable} onChange={setLabelVariable}>
                  {allowedVariables.map(item => (
                    <Option value={item.id}>{item.placeHolderText}</Option>
                  ))}
                </ChartSelect>
              </label>
              <label>
                <span>
                  {t(
                    CHART_TRANSLATION.SERIES_NAME_LABEL.key,
                    CHART_TRANSLATION.SERIES_NAME_LABEL.value,
                  )}
                </span>
                <ChartSelect t={t} value={seriesNameVariable} onChange={setSeriesNameVariable}>
                  {allowedVariables.map(item => (
                    <Option value={item.id}>{item.placeHolderText}</Option>
                  ))}
                </ChartSelect>
              </label>
              <label>
                <span>
                  {t(
                    CHART_TRANSLATION.SERIES_VALUE_LABEL.key,
                    CHART_TRANSLATION.SERIES_VALUE_LABEL.value,
                  )}
                </span>
                <ChartSelect t={t} value={seriesValueVariable} onChange={setSeriesValueVariable}>
                  {allowedVariables.map(item => (
                    <Option value={item.id}>{item.placeHolderText}</Option>
                  ))}
                </ChartSelect>
              </label>
            </div>
          )}
        </ChartDataTab>
      </TabPane>
      <TabPane
        tab={t(CHART_TRANSLATION.SETTINGS_LABEL.key, CHART_TRANSLATION.SETTINGS_LABEL.value)}
        key="2"
      >
        <CollapseContainer>
          <ChartCollapse defaultActiveKey="1">
            <CollapsePanel
              header={
                <div>
                  <Icon component={GeneralSettingsIcon} />{' '}
                  <span>
                    {t(CHART_TRANSLATION.GENERAL_LABEL.key, CHART_TRANSLATION.GENERAL_LABEL.value)}
                  </span>
                  <span>
                    <Icon component={ArrowDown} />
                  </span>
                </div>
              }
              key="1"
              style={panelStyle}
            >
              <ChartGeneralSettings>
                <label id="bar-format-values">
                  <span>
                    {t(
                      CHART_TRANSLATION.FORMAT_VALUES_LABEL.key,
                      CHART_TRANSLATION.FORMAT_VALUES_LABEL.value,
                    )}
                    <Tooltip>
                      <Icon type="info-circle" />
                    </Tooltip>
                  </span>
                  <div>
                    <Input value={prefixStateValue} onChange={setPrefixValue} />
                    <span>
                      {t(CHART_TRANSLATION.VALUE_LABEL.key, CHART_TRANSLATION.VALUE_LABEL.value)}
                    </span>
                    <Input value={suffixStateValue} onChange={setSuffixValue} />
                  </div>
                </label>
                <label>
                  <span>
                    {t(
                      CHART_TRANSLATION.INTERVAL_LABEL.key,
                      CHART_TRANSLATION.INTERVAL_LABEL.value,
                    )}
                    <Tooltip>
                      <Icon type="info-circle" />
                    </Tooltip>
                  </span>
                  <InputNumber value={rangeIntervalValue} onChange={setIntervalValue} />
                </label>
                <label>
                  <span>
                    {t(
                      CHART_TRANSLATION.SHOW_LEGEND_LABEL.key,
                      CHART_TRANSLATION.SHOW_LEGEND_LABEL.value,
                    )}
                  </span>
                  <Switch checked={showLegend} onChange={checked => toggleLegend(checked)} />
                </label>
                <label>
                  <span>
                    {t(
                      CHART_TRANSLATION.SHOW_GRID_LABEL.key,
                      CHART_TRANSLATION.SHOW_GRID_LABEL.value,
                    )}
                  </span>
                  <Switch checked={showGrid} onChange={checked => toggleGrid(checked)} />
                </label>
                <label>
                  <span>
                    {t(
                      CHART_TRANSLATION.SHOW_LABELS_LABEL.key,
                      CHART_TRANSLATION.SHOW_LABELS_LABEL.value,
                    )}
                  </span>
                  <Switch checked={showLabels} onChange={checked => toggleLabels(checked)} />
                </label>
                <label className="width-100">
                  <span>
                    {t(
                      CHART_TRANSLATION.ORIENTATION_LABEL.key,
                      CHART_TRANSLATION.ORIENTATION_LABEL.value,
                    )}
                  </span>
                  <Select
                    value={orientation}
                    onSelect={value => setOrientation(value)}
                    suffixIcon={<Icon type="caret-down" />}
                  >
                    <Option value="vertical">
                      {t(
                        CHART_TRANSLATION.VERTICAL_LABEL.key,
                        CHART_TRANSLATION.VERTICAL_LABEL.value,
                      )}
                    </Option>
                    <Option value="horizontal">
                      {t(
                        CHART_TRANSLATION.HORIZONTAL_LABEL.key,
                        CHART_TRANSLATION.HORIZONTAL_LABEL.value,
                      )}
                    </Option>
                  </Select>
                </label>
                {showLegend && (
                  <label>
                    <span>
                      {t(
                        CHART_TRANSLATION.LEGEND_POSITION_LABEL.key,
                        CHART_TRANSLATION.LEGEND_POSITION_LABEL.value,
                      )}
                    </span>
                    <Select
                      value={legendPosition}
                      onSelect={value => setLegendPosition(value)}
                      suffixIcon={<Icon type="caret-down" />}
                    >
                      <Option value="top">
                        {t(CHART_TRANSLATION.TOP_LABEL.key, CHART_TRANSLATION.TOP_LABEL.value)}
                      </Option>
                      <Option value="bottom">
                        {t(
                          CHART_TRANSLATION.BOTTOM_LABEL.key,
                          CHART_TRANSLATION.BOTTOM_LABEL.value,
                        )}
                      </Option>
                    </Select>
                  </label>
                )}
              </ChartGeneralSettings>
            </CollapsePanel>
          </ChartCollapse>
          <ChartCollapse>
            <CollapsePanel
              header={
                <div>
                  <Icon component={FontSettingsIcon} />{' '}
                  <span>
                    {t(
                      CHART_TRANSLATION.FONTSTYLES_LABEL.key,
                      CHART_TRANSLATION.FONTSTYLES_LABEL.value,
                    )}
                  </span>
                  <span>
                    <Icon component={ArrowDown} />
                  </span>
                </div>
              }
              key="1"
              style={panelStyle}
            >
              <ChartFontsizeSettings>
                <label onClick={e => e.preventDefault()}>
                  <span>{t(CHART_TRANSLATION.LABEL.key, CHART_TRANSLATION.LABEL.value)}</span>
                  <FontstyleCombobox
                    t={t}
                    onSizeChange={setLabelFontsize}
                    fontColor={labelColor}
                    fontSize={labelFontSize}
                    onColorChange={setLabelFontColor}
                  />
                </label>
                <label onClick={e => e.preventDefault()}>
                  <span>
                    {t(CHART_TRANSLATION.VALUE_LABEL.key, CHART_TRANSLATION.VALUE_LABEL.value)}
                  </span>
                  <FontstyleCombobox
                    t={t}
                    onSizeChange={setValueFontsize}
                    fontColor={valueColor}
                    fontSize={valueFontSize}
                    onColorChange={setValueFontColor}
                  />
                </label>
                <label onClick={e => e.preventDefault()}>
                  <span>
                    {t(CHART_TRANSLATION.LEGEND_LABEL.key, CHART_TRANSLATION.LEGEND_LABEL.value)}
                  </span>
                  <FontstyleCombobox
                    t={t}
                    onSizeChange={setLegendFontsize}
                    fontColor={legendColor}
                    fontSize={legendFontSize}
                    onColorChange={setLegendFontColor}
                  />
                </label>
              </ChartFontsizeSettings>
            </CollapsePanel>
          </ChartCollapse>
          <ChartCollapse>
            <CollapsePanel
              header={
                <div>
                  <Icon component={ChartTypesIcon} />{' '}
                  <span>
                    {t(
                      CHART_TRANSLATION.CHART_TYPE_LABEL.key,
                      CHART_TRANSLATION.CHART_TYPE_LABEL.value,
                    )}
                  </span>
                  <span>
                    <Icon component={ArrowDown} />
                  </span>
                </div>
              }
              key="1"
              style={panelStyle}
            >
              <ChartTypeSettings>
                {chartTypesList.map(item => (
                  <ChartTypeContainer
                    active={item.type === type}
                    onClick={() => setChartType(item)}
                  >
                    <img src={item.src} alt={item.alt} />
                    <div>{item.tag}</div>
                  </ChartTypeContainer>
                ))}
              </ChartTypeSettings>
            </CollapsePanel>
          </ChartCollapse>
          <ChartCollapse>
            <CollapsePanel
              header={
                <div>
                  <Icon component={chartStylesIcon} />{' '}
                  <span>
                    {t(
                      CHART_TRANSLATION.CHART_STYLE_LABEL.key,
                      CHART_TRANSLATION.CHART_STYLE_LABEL.value,
                    )}
                  </span>
                  <span>
                    <Icon component={ArrowDown} />
                  </span>
                </div>
              }
              key="1"
              style={panelStyle}
            >
              <ChartStylesSettings>
                <Radio.Group value={chartStyle} onChange={e => setChartStyle(e.target.value)}>
                  {chartStyles.map(item => (
                    <Radio.Button value={item.value}>{item.label}</Radio.Button>
                  ))}
                </Radio.Group>
              </ChartStylesSettings>
            </CollapsePanel>
          </ChartCollapse>
        </CollapseContainer>
      </TabPane>
    </Tabs>
  );
};
